import { backendApi } from "./backendApi.rtkq";
import { adminUsersApi } from "./adminUsers.rtkq";

export interface AdminOffice {
  id: string;
  name: string;
  slug: string;
  owner?: { id: string; displayName: string; email: string };
  memberCount: number;
  subscriptionStatus: string;
  billingStatus?: string;
  autoRenew?: boolean;
  subscriptionCurrentPeriodEnd?: string | null;
  subscriptionStartDate?: string | null;
  periodDays?: number | null;
  subscriptionPlan?: {
    id: string;
    name: string;
    internalName: string;
    periodDays?: number;
    isActive?: boolean;
  };
  creditBalance: number;
  appBonusCreditBalances?: Record<string, number>;
  bonusCreditBalance?: number;
  quotaWindowStart?: string | null;
  quotaWindowEnd?: string | null;
  quotaWindowDays?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOfficePlanApp {
  appIdentifier: string;
  includedCredits?: number;
}

export interface AdminOfficeDetail extends AdminOffice {
  ownerUserId: string;
  accountInformation: Record<string, any>;
  appCreditBalances: Record<string, number>;
  appBonusCreditBalances?: Record<string, number>;
  quotaWindowStart?: string | null;
  quotaWindowEnd?: string | null;
  subscriptionPlanId?: string;
  subscriptionPlan?: {
    id: string;
    name: string;
    internalName: string;
    includedApps?: AdminOfficePlanApp[];
    quotaWindowDays?: number;
    periodDays?: number;
  };
  members: {
    userId: string;
    displayName: string;
    email: string;
    role: string;
    isActive: boolean;
    createdAt: string;
  }[];
}

export interface UpdateOfficeCreditsParams {
  officeId: string;
  newAmount?: number;
  appId?: string;
  appCreditBalances?: Record<string, number>;
  windowCreditBalances?: Record<string, number>;
}

export interface AdminOfficeMember {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

export const adminOfficesApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminOffices: builder.query<AdminOffice[], void>({
      query: () => "/admin/offices",
      providesTags: ["Accounts"],
    }),

    getAdminOffice: builder.query<AdminOfficeDetail, string>({
      query: (id) => `/admin/offices/${id}`,
      providesTags: (_result, _err, id) => [{ type: "Accounts", id }],
    }),

    createAdminOffice: builder.mutation<any, { name: string; ownerUserId: string; planId?: string }>({
      query: (body) => ({ url: "/admin/offices", method: "POST", body }),
      invalidatesTags: ["Accounts"],
    }),

    updateAdminOffice: builder.mutation<any, { id: string; updates: Record<string, any> }>({
      query: ({ id, updates }) => ({ url: `/admin/offices/${id}`, method: "PUT", body: updates }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Accounts", id }, "Accounts"],
    }),

    deleteAdminOffice: builder.mutation<{ success: boolean }, { id: string; deleteMembers?: boolean }>({
      query: ({ id, deleteMembers }) => ({
        url: `/admin/offices/${id}${deleteMembers ? '?deleteMembers=true' : ''}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Accounts", "Users"],
    }),

    addOfficeMember: builder.mutation<any, { officeId: string; userId: string; role: string }>({
      query: ({ officeId, ...body }) => ({
        url: `/admin/offices/${officeId}/members`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { officeId }) => [
        { type: "Accounts", id: officeId },
        "Accounts",
        "Users",
      ],
    }),

    removeOfficeMember: builder.mutation<{ success: boolean }, { officeId: string; userId: string }>({
      query: ({ officeId, userId }) => ({
        url: `/admin/offices/${officeId}/members/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { officeId }) => [
        { type: "Accounts", id: officeId },
        "Accounts",
        "Users",
      ],
    }),

    updateOfficeMemberRole: builder.mutation<any, { officeId: string; userId: string; role: string }>({
      query: ({ officeId, userId, role }) => ({
        url: `/admin/offices/${officeId}/members/${userId}/role`,
        method: "PUT",
        body: { role },
      }),
      async onQueryStarted({ officeId, userId, role }, { dispatch, queryFulfilled }) {
        const patchUsers = dispatch(
          adminUsersApi.util.updateQueryData("getAdminUsers", undefined, (draft) => {
            const user = draft.find((entry) => entry.id === userId);
            const membership = user?.offices.find((office) => office.officeId === officeId);
            if (membership) membership.role = role;
          }),
        );
        const patchOffice = dispatch(
          adminOfficesApi.util.updateQueryData("getAdminOffice", officeId, (draft) => {
            const member = draft.members?.find((entry) => entry.userId === userId);
            if (member) member.role = role;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchUsers.undo();
          patchOffice.undo();
        }
      },
      // Cache is patched above; skip refetch so the Select does not snap back.
      invalidatesTags: [],
    }),

    transferOfficeOwnership: builder.mutation<any, { officeId: string; newOwnerUserId: string }>({
      query: ({ officeId, newOwnerUserId }) => ({
        url: `/offices/${officeId}/transfer-ownership`,
        method: "POST",
        body: { newOwnerUserId },
      }),
      invalidatesTags: ["Accounts"],
    }),

    updateOfficeSubscription: builder.mutation<any, { officeId: string; planId: string }>({
      query: ({ officeId, planId }) => ({
        url: `/offices/${officeId}/subscription`,
        method: "PUT",
        body: { planId },
      }),
      invalidatesTags: ["Accounts", "Dashboard"],
    }),

    updateOfficeCredits: builder.mutation<any, UpdateOfficeCreditsParams>({
      query: ({ officeId, newAmount, appId, appCreditBalances, windowCreditBalances }) => ({
        url: `/offices/${officeId}/credits`,
        method: "PUT",
        body: {
          ...(newAmount !== undefined ? { newAmount } : {}),
          ...(appId ? { appId } : {}),
          ...(appCreditBalances ? { appCreditBalances } : {}),
          ...(windowCreditBalances ? { windowCreditBalances } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { officeId }) => [
        { type: "Accounts", id: officeId },
        "Accounts",
        "Dashboard",
      ],
    }),

    refreshOfficeQuotaWindow: builder.mutation<
      {
        officeId: string;
        refilled: boolean;
        creditBalance: number;
        appCreditBalances: Record<string, number>;
        appBonusCreditBalances: Record<string, number>;
        quotaWindowStart?: string;
        quotaWindowEnd?: string;
      },
      { officeId: string }
    >({
      query: ({ officeId }) => ({
        url: `/offices/${officeId}/quota-window/refresh`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { officeId }) => [
        { type: "Accounts", id: officeId },
        "Accounts",
        "Dashboard",
      ],
    }),
    extendOfficeSubscription: builder.mutation<
      {
        officeId: string;
        status: string;
        subscriptionCurrentPeriodEnd?: string | null;
        quotaWindowEnd?: string | null;
        creditBalance: number;
      },
      { officeId: string; months?: number; days?: number; endsAt?: string }
    >({
      query: ({ officeId, ...body }) => ({
        url: `/offices/${officeId}/subscription/extend`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { officeId }) => [
        { type: "Accounts", id: officeId },
        "Accounts",
        "Dashboard",
      ],
    }),
  }),
});

export const {
  useGetAdminOfficesQuery,
  useGetAdminOfficeQuery,
  useCreateAdminOfficeMutation,
  useUpdateAdminOfficeMutation,
  useDeleteAdminOfficeMutation,
  useAddOfficeMemberMutation,
  useRemoveOfficeMemberMutation,
  useUpdateOfficeMemberRoleMutation,
  useTransferOfficeOwnershipMutation,
  useUpdateOfficeSubscriptionMutation,
  useUpdateOfficeCreditsMutation,
  useRefreshOfficeQuotaWindowMutation,
  useExtendOfficeSubscriptionMutation,
} = adminOfficesApi;
